import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type { ItemClickedDetail } from '../interfaces/event-detail.types.js';
import { ITEM_CLICKED_EVENT, ITEM_SELECTED_EVENT } from '../interfaces/event-detail.types.js';

/**
 * Reactive controller for single item selection.
 * Listens for item-clicked events on the host, dispatches item-selected,
 * then notifies the popover element and closes it.
 */
export class SingleSelectionController implements ReactiveController {

    private _host: ReactiveControllerHost & EventTarget;
    private _popoverElement: HTMLElement & { notifyItemSelected?: () => void };

    constructor(
        host: ReactiveControllerHost & EventTarget,
        popoverElement: HTMLElement & { notifyItemSelected?: () => void },
    ) {
        this._host = host;
        this._popoverElement = popoverElement;
        host.addController(this);

        this._handleItemClicked = this._handleItemClicked.bind(this);
        (host as EventTarget).addEventListener(ITEM_CLICKED_EVENT, this._handleItemClicked as EventListener);
    }

    /** Required by ReactiveController interface. No-op — setup done in constructor. */
    hostConnected(): void {
        // Listener already attached in constructor
    }

    /**
     * Handles item-clicked events: dispatches item-selected,
     * calls notifyItemSelected() BEFORE hidePopover().
     */
    private _handleItemClicked(event: CustomEvent<ItemClickedDetail>): void {
        const { item } = event.detail;

        this._host.dispatchEvent(new CustomEvent(ITEM_SELECTED_EVENT, {
            bubbles: true,
            composed: true,
            detail: { item },
        }));

        this._popoverElement.notifyItemSelected?.();
        if (this._popoverElement.hasAttribute('popover')) {
            this._popoverElement.hidePopover();
        }
    }
}
